/**
 * @file content-progress.schema.ts
 * @description Define o schema Zod para a entidade 'ContentProgress'.
 * Cada documento na coleção 'contentProgress' representa o progresso de um usuário
 * em uma única peça de conteúdo (vídeo, revista, etc.), vinculada a uma gestação específica.
 */

import { z } from 'zod';

// --- Helper Schemas & Enums ---

/**
 * Define os tipos de conteúdo cujo progresso pode ser rastreado individualmente.
 * Como são categorias, usamos UPPER_SNAKE_CASE.
 */
const ProgressContentTypeEnum = z.enum([
  'VIDEO',
  'MAGAZINE',
  'PARTNER_CONTENT',
  'COURSE', // Adicionando Curso para poder marcá-lo como 'concluído' no geral
]);

/**
 * Define o status de consumo de um conteúdo.
 * Como são estados de um fluxo, usamos camelCase.
 */
const ProgressStatusEnum = z.enum(['notStarted', 'inProgress', 'completed']);


// --- Schemas de Progresso (União Discriminada) ---

/**
 * Schema base com campos comuns a todo tipo de progresso.
 */
const BaseProgressSchema = z.object({
  // Chave estrangeira que liga este progresso à gestação correspondente.
  /**
   * OPCIONAL desde A035.
   *
   * O progresso nasceu preso à gestação porque só a paciente consumia
   * conteúdo. Curso de formação profissional não tem gestação nenhuma — e
   * exigir o campo faria a profissional não conseguir marcar aula assistida.
   *
   * Continua obrigatório na prática para conteúdo de paciente: quem grava é
   * quem sabe, e lá a gestação sempre existe.
   */
  pregnancyId: z.string().optional(),

  // ID do conteúdo que está sendo consumido (pode ser ID do curso, vídeo, revista, etc.).
  contentId: z.string(),

  // ID do usuário (para referência e regras de segurança).
  userId: z.string(),
  
  // Status geral do consumo.
  status: ProgressStatusEnum,
  
  // Para ordenar por "vistos recentemente".
  lastAccessedAt: z.date(),

  // Primeiro acesso ao conteúdo (para calcular tempo até conclusão).
  firstAccessedAt: z.date().optional(),

  // Data de conclusão do conteúdo.
  completedAt: z.date().nullable().optional(),

  // Campos de auditoria.
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

/**
 * Rastreia o progresso específico de um vídeo.
 */
const VideoProgressSchema = BaseProgressSchema.extend({
  type: z.literal('VIDEO'),
  // Minutagem em segundos onde a usuária parou.
  progressSeconds: z.number().int().min(0),
  // IDs para dar contexto de onde este vídeo veio.
  courseId: z.string(),
  moduleId: z.string(),
  // Métricas de engajamento acumuladas.
  totalScreenTimeSeconds: z.number().int().min(0).optional(),
  sessionCount: z.number().int().min(0).optional(),
});

/**
 * Rastreia o progresso de entidades que são apenas lidas (status é suficiente).
 */
const SimpleContentProgressSchema = BaseProgressSchema.extend({
  type: z.enum(['MAGAZINE', 'PARTNER_CONTENT', 'COURSE']),
});


/**
 * O schema final para um documento de progresso de conteúdo, unindo as possibilidades.
 */
const ContentProgressSchema = z.discriminatedUnion('type', [
  VideoProgressSchema,
  SimpleContentProgressSchema,
]);


// --- Inferred TypeScript Types ---

type ContentProgress = z.infer<typeof ContentProgressSchema>;
type ProgressContentType = z.infer<typeof ProgressContentTypeEnum>;
type ProgressStatus = z.infer<typeof ProgressStatusEnum>;
type VideoProgress = z.infer<typeof VideoProgressSchema>;
type SimpleContentProgress = z.infer<typeof SimpleContentProgressSchema>;


// --- Public Exports ---

export {
  ContentProgressSchema,
  ProgressContentTypeEnum,
  ProgressStatusEnum,
  VideoProgressSchema,
  SimpleContentProgressSchema,
};

export type {
  ContentProgress,
  ProgressContentType,
  ProgressStatus,
  VideoProgress,
  SimpleContentProgress,
};